import { Modal } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import {
  useCreateDepartment,
  useDepartmentsQuery,
  useUpdateDepartment,
} from "../api/queries";
import { DepartmentForm } from "../ui/DepartmentForm";
import { DepartmentPositionsModal } from "../ui/DepartmentPositionsModal";
import { DepartmentsFilter } from "../ui/DepartmentsFilter";
import { DepartmentsTable } from "../ui/DepartmentsTable";
import { DepartmentsToolbar } from "../ui/DepartmentsToolbar";

export function DepartmentsPage() {
  const nav = useNavigate({ from: "/app/departments" });
  const search: OffsetPageQueryDTO = useSearch({ from: "/app/departments" });

  const list = useDepartmentsQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const canManage = useActionPermission(["departments:create"]);
  const createDepartment = useCreateDepartment();
  const editModal = useDisclosure<{
    id: string;
    name: string;
    managerId: string | null;
  }>();
  const updateDepartment = useUpdateDepartment(editModal.data?.id ?? "");
  const createModal = useDisclosure();
  const positionsModal = useDisclosure<{
    id: string;
    name: string;
  }>();

  return (
    <>
      <Header />

      <Main>
        <DepartmentsToolbar
          canManage={!!canManage}
          onCreate={createModal.open}
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <DepartmentsFilter />

          <DepartmentsTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onEdit={(d) =>
              editModal.openWith({
                id: d.id,
                name: d.name,
                managerId: d.managerId,
              })
            }
            onViewPositions={(d) =>
              positionsModal.openWith({
                id: d.id,
                name: d.name,
              })
            }
          />
        </div>

        <Modal
          open={!!createModal.isOpen}
          onOpenChange={createModal.toggle}
          title="ສ້າງພະແນກ"
          size="sm"
        >
          <div className="min-w-0 max-w-full overflow-hidden">
            <DepartmentForm
              onSubmit={async (vals) => {
                await createDepartment.mutateAsync({
                  name: vals.name,
                  managerId: vals.managerId ?? null,
                });
                createModal.close();
              }}
              submitting={createDepartment.isPending}
            />
          </div>
        </Modal>

        <Modal
          open={!!editModal.isOpen}
          onOpenChange={editModal.toggle}
          title="ແກ້ໄຂພະແນກ"
          size="sm"
        >
          <div className="min-w-0 max-w-full overflow-hidden">
            <DepartmentForm
              initialValues={editModal.data ?? undefined}
              onSubmit={async (vals) => {
                await updateDepartment.mutateAsync({
                  name: vals.name,
                  managerId: vals.managerId ?? null,
                });
                editModal.close();
              }}
              submitting={updateDepartment.isPending}
            />
          </div>
        </Modal>

        <DepartmentPositionsModal
          open={!!positionsModal.isOpen}
          onOpenChange={positionsModal.toggle}
          departmentId={positionsModal.data?.id}
          departmentName={positionsModal.data?.name}
        />
      </Main>
    </>
  );
}
